import type { ExternalProvider } from '@ethersproject/providers';
import { Web3Provider } from '@ethersproject/providers';
import { Connector, ConnectorEvent, ConnectorUpdateData } from '@usedapp/core';

type EIP1193Provider = {
  request: (args: { method: string; params?: unknown[] }) => Promise<unknown>;
  on?: (event: string, callback: (...args: unknown[]) => void) => void;
  removeListener?: (
    event: string,
    callback: (...args: unknown[]) => void,
  ) => void;
};

/**
 * Create Web3Provider only after we have chainId from the wallet.
 * Creating it with explicit network avoids ethers "could not detect network"
 * (NETWORK_ERROR), same as RabbyConnector/other connectors in this folder.
 */
function createWeb3Provider(
  raw: EIP1193Provider,
  chainId: number,
): Web3Provider {
  const network = {
    chainId,
    name: chainId === 1 ? 'mainnet' : `chain-${chainId}`,
  };
  return new Web3Provider(raw as unknown as ExternalProvider, network);
}

/**
 * Bridges whatever wallet thirdweb's <ConnectButton> connects into
 * @usedapp/core, so every existing useEthers()/useConnector() consumer in the
 * app keeps working unchanged. Unlike RabbyConnector/MetamaskConnector, this
 * connector never discovers a provider on its own — it's entirely driven by
 * ThirdwebSync, which stages a fresh EIP-1193 provider (derived from
 * thirdweb's active wallet + chain) before triggering activation.
 */
export class ThirdwebBridgeConnector implements Connector {
  public provider?: Web3Provider;

  public readonly name = 'thirdweb';

  readonly update = new ConnectorEvent<ConnectorUpdateData>();

  private staged?: {
    raw: EIP1193Provider;
    chainId: number;
    accounts: string[];
  };

  /** Record the provider to use on the next activate() call. */
  stage(raw: EIP1193Provider, chainId: number, accounts: string[]): void {
    this.staged = { raw, chainId, accounts };
  }

  /** Push a provider/chain/account update while already the active connector (e.g. a network switch). */
  sync(raw: EIP1193Provider, chainId: number, accounts: string[]): void {
    this.stage(raw, chainId, accounts);
    this.provider = createWeb3Provider(raw, chainId);
    this.update.emit({ chainId, accounts });
  }

  async activate(): Promise<void> {
    if (!this.staged) {
      throw new Error('ThirdwebBridgeConnector: no wallet staged to activate');
    }
    const { raw, chainId, accounts } = this.staged;
    this.provider = createWeb3Provider(raw, chainId);
    this.update.emit({ chainId, accounts });
  }

  // eslint-disable-next-line class-methods-use-this
  async connectEagerly(): Promise<void> {
    // Eager reconnect on page load is handled by thirdweb's own autoConnect,
    // which re-triggers ThirdwebSync's effect once it restores a session.
  }

  async deactivate(): Promise<void> {
    this.staged = undefined;
    this.provider = undefined;
  }
}

export const thirdwebBridgeConnector = new ThirdwebBridgeConnector();
